/* ──────────────────────────────────────────────────────────────────────
   Main product section — three custom elements + a small gallery hook.
   <variant-selects>  – resolves selected variant, updates price/image/URL
   <quantity-input>   – +/- stepper bound to a variant's quantity rules
   <product-form>     – intercepts submit, shows error/success states
   ────────────────────────────────────────────────────────────────────── */

(function () {
  'use strict';

  const PUB = {
    variantChange: 'dahabeya:variant:change',
    cartAdd: 'dahabeya:cart:add',
    cartError: 'dahabeya:cart:error'
  };

  function formatMoney(cents) {
    if (cents == null || isNaN(cents)) return '';
    const n = cents / 100;
    return Number.isInteger(n) ? String(n) : n.toFixed(2);
  }

  // ─── <variant-selects> ──────────────────────────────────────────────
  if (!customElements.get('variant-selects')) {
    class VariantSelects extends HTMLElement {
      connectedCallback() {
        const json = this.querySelector('[data-product-json]');
        try {
          this.variantData = JSON.parse(json.textContent || '[]');
        } catch (e) {
          this.variantData = [];
        }
        this.section = this.closest('.section-main-product') || document;
        this.sectionId = this.dataset.section;
        this.url = this.dataset.url;
        this.currentVariant = this.resolveFromSelection();
        this.addEventListener('change', this.onChange.bind(this));
      }

      resolveFromSelection() {
        const selectedValues = this.collectSelectedValues();
        if (selectedValues.length === 0) return null;
        return this.variantData.find((variant) =>
          variant.options.every((opt, i) => opt === selectedValues[i])
        );
      }

      collectSelectedValues() {
        const groups = this.querySelectorAll('[data-option-position]');
        const values = [];
        groups.forEach((group) => {
          const position = parseInt(group.dataset.optionPosition, 10);
          let value = null;
          if (group.matches('select')) {
            value = group.value;
          } else if (group.tagName === 'FIELDSET') {
            const checked = group.querySelector('input[type="radio"]:checked');
            if (checked) value = checked.value;
          }
          values[position - 1] = value;
        });
        return values;
      }

      onChange(event) {
        const target = event.target;
        if (target && target.dataset && target.dataset.unavailable === 'true') {
          return;
        }
        this.currentVariant = this.resolveFromSelection();
        this.updateLegendValues();
        this.updateVariantIdInput();
        this.updatePriceBlock();
        this.updateSubmitState();
        this.updateImage();
        this.updateUrl();
        this.notify();
      }

      updateLegendValues() {
        this.querySelectorAll('[data-selected-value]').forEach((el) => {
          const fieldset = el.closest('fieldset[data-option-position]');
          if (!fieldset) return;
          const checked = fieldset.querySelector('input[type="radio"]:checked');
          if (checked) el.textContent = checked.value;
        });
      }

      updateVariantIdInput() {
        const idInput = this.section.querySelector('[data-variant-id-input]');
        if (!idInput) return;
        if (this.currentVariant) {
          idInput.value = this.currentVariant.id;
          idInput.removeAttribute('disabled');
        } else {
          idInput.value = '';
          idInput.setAttribute('disabled', 'disabled');
        }
      }

      updatePriceBlock() {
        const variant = this.currentVariant;
        const priceEl = this.section.querySelector('[data-price-amount]');
        const compareEl = this.section.querySelector('[data-price-compare]');
        const perKgEl = this.section.querySelector('[data-price-perkg]');
        if (variant && priceEl) {
          priceEl.textContent = formatMoney(variant.price);
        }
        if (compareEl) {
          if (variant && variant.compare_at_price && variant.compare_at_price > variant.price) {
            compareEl.textContent = formatMoney(variant.compare_at_price);
            compareEl.hidden = false;
          } else {
            compareEl.hidden = true;
          }
        }
        if (perKgEl) {
          const tpl = this.section.dataset.perkgTemplate || '';
          if (variant && variant.weight > 0 && tpl) {
            const perKg = Math.round((variant.price * 1000) / variant.weight);
            perKgEl.textContent = tpl.replace('__AMOUNT__', formatMoney(perKg));
            perKgEl.hidden = false;
          } else if (perKgEl) {
            perKgEl.hidden = true;
          }
        }
      }

      updateSubmitState() {
        const variant = this.currentVariant;
        const submit = this.section.querySelector('[data-add-button]');
        const label = this.section.querySelector('[data-submit-label]');
        const submitTotal = this.section.querySelector('[data-submit-total]');
        if (!submit) return;
        if (!variant) {
          submit.disabled = true;
          if (label) label.textContent = submit.dataset.unavailableText || '';
          return;
        }
        if (!variant.available) {
          submit.disabled = true;
          if (label) label.textContent = submit.dataset.soldOutText || '';
          return;
        }
        submit.disabled = false;
        // Rebuild ATC label "Add to cart · 120 ج.م"
        if (label) {
          const qtyInput = this.section.querySelector('[data-quantity-input]');
          const qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
          const total = formatMoney(variant.price * qty);
          const tpl = submit.dataset.labelTemplate;
          if (tpl) {
            label.textContent = tpl.replace('__TOTAL__', total + ' ');
          }
        }
      }

      updateImage() {
        const variant = this.currentVariant;
        if (!variant || !variant.featured_image) return;
        // `featured_image` can be a string URL or an object depending on Shopify version.
        const fi = variant.featured_image;
        const newId = typeof fi === 'object' && fi ? fi.id : null;
        if (!newId) return;
        // Find the slide whose media id matches the variant's featured image
        // and ask the gallery to scroll to it.
        const slide = this.section.querySelector('[data-slide][data-image-id="' + newId + '"]');
        if (!slide) return;
        const targetIndex = parseInt(slide.dataset.index, 10);
        if (!isNaN(targetIndex) && window.__dahabeyaProductGallery) {
          window.__dahabeyaProductGallery(targetIndex);
        }
      }

      updateUrl() {
        if (!this.currentVariant || !window.history || !window.history.replaceState) return;
        try {
          const url = new URL(window.location);
          url.searchParams.set('variant', this.currentVariant.id);
          window.history.replaceState({}, '', url.toString());
        } catch (e) { /* noop */ }
      }

      notify() {
        document.dispatchEvent(new CustomEvent(PUB.variantChange, {
          detail: {
            variant: this.currentVariant,
            sectionId: this.sectionId
          }
        }));
      }
    }
    customElements.define('variant-selects', VariantSelects);
  }

  // ─── <quantity-input> ───────────────────────────────────────────────
  if (!customElements.get('quantity-input')) {
    class QuantityInput extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('[data-quantity-input]');
        this.minusBtn = this.querySelector('[data-quantity-minus]');
        this.plusBtn = this.querySelector('[data-quantity-plus]');
        if (!this.input) return;
        this.step = parseInt(this.dataset.step, 10) || 1;
        this.min = parseInt(this.dataset.min, 10) || 1;
        this.max = this.dataset.max ? parseInt(this.dataset.max, 10) : null;
        this.input.addEventListener('change', this.onChange.bind(this));
        this.input.addEventListener('input', this.onChange.bind(this));
        if (this.minusBtn) this.minusBtn.addEventListener('click', this.dec.bind(this));
        if (this.plusBtn) this.plusBtn.addEventListener('click', this.inc.bind(this));
        this.validate();
      }
      currentValue() {
        return parseInt(this.input.value, 10) || this.min;
      }
      setValue(v) {
        let next = v;
        if (this.max !== null) next = Math.min(this.max, next);
        next = Math.max(this.min, next);
        this.input.value = next;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      inc() { this.setValue(this.currentValue() + this.step); }
      dec() { this.setValue(this.currentValue() - this.step); }
      onChange() { this.validate(); }
      validate() {
        const v = this.currentValue();
        if (this.minusBtn) this.minusBtn.disabled = v <= this.min;
        if (this.plusBtn && this.max !== null) this.plusBtn.disabled = v >= this.max;
      }
    }
    customElements.define('quantity-input', QuantityInput);
  }

  // ─── <product-form> ─────────────────────────────────────────────────
  if (!customElements.get('product-form')) {
    class ProductForm extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        this.button = this.querySelector('[data-add-button]');
        this.errorEl = this.querySelector('[data-form-error]');
        this.errorMsg = this.querySelector('[data-form-error-message]');
        this.section = this.closest('.section-main-product') || document;
        if (this.form) this.form.addEventListener('submit', this.onSubmit.bind(this));
        // Update ATC label when qty changes
        const qty = this.section.querySelector('[data-quantity-input]');
        if (qty) qty.addEventListener('change', this.refreshLabel.bind(this));
      }

      refreshLabel() {
        const selects = this.section.querySelector('variant-selects');
        if (selects && typeof selects.updateSubmitState === 'function') {
          selects.updateSubmitState();
        }
      }

      onSubmit(event) {
        event.preventDefault();
        if (!this.button || this.button.disabled) return;
        this.hideError();
        this.setLoading(true);
        const formData = new FormData(this.form);
        // Add a section-rendering hint Shopify accepts (cart sections refresh)
        formData.append('sections_url', window.location.pathname);
        fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          body: formData,
          headers: { Accept: 'application/javascript' }
        })
          .then((r) => r.json().then((data) => ({ ok: r.ok, status: r.status, data })))
          .then(({ ok, data }) => {
            if (!ok || data.status) {
              this.showError(data.description || data.message || (window.__dahabeya_t_generic_error || 'حصلت مشكلة'));
              this.setLoading(false);
              document.dispatchEvent(new CustomEvent(PUB.cartError, { detail: data }));
              return;
            }
            this.setSuccess();
            // PUB.cartAdd stays on document — sticky-cart-bar listens there for internal coupling.
            document.dispatchEvent(new CustomEvent(PUB.cartAdd, { detail: data }));
            // cart-drawer.liquid listens on window — must dispatch there or the drawer never opens.
            window.dispatchEvent(new CustomEvent('cart:updated', { detail: { item: data } }));
            window.dispatchEvent(new CustomEvent('cart:open'));
            window.setTimeout(() => {
              this.resetButton();
            }, 1800);
          })
          .catch(() => {
            this.showError(window.__dahabeya_t_generic_error || 'حصلت مشكلة');
            this.setLoading(false);
          });
      }

      setLoading(loading) {
        if (!this.button) return;
        if (loading) {
          this.button.dataset.loading = 'true';
          this.button.disabled = true;
        } else {
          delete this.button.dataset.loading;
          this.button.disabled = false;
        }
        const label = this.button.querySelector('[data-submit-label]');
        const spinner = this.button.querySelector('[data-submit-spinner]');
        if (label) label.hidden = loading;
        if (spinner) spinner.hidden = !loading;
      }

      setSuccess() {
        if (!this.button) return;
        delete this.button.dataset.loading;
        this.button.dataset.success = 'true';
        const label = this.button.querySelector('[data-submit-label]');
        const spinner = this.button.querySelector('[data-submit-spinner]');
        const success = this.button.querySelector('[data-submit-success]');
        if (label) label.hidden = true;
        if (spinner) spinner.hidden = true;
        if (success) success.hidden = false;
      }

      resetButton() {
        if (!this.button) return;
        delete this.button.dataset.success;
        delete this.button.dataset.loading;
        this.button.disabled = false;
        const label = this.button.querySelector('[data-submit-label]');
        const spinner = this.button.querySelector('[data-submit-spinner]');
        const success = this.button.querySelector('[data-submit-success]');
        if (label) label.hidden = false;
        if (spinner) spinner.hidden = true;
        if (success) success.hidden = true;
      }

      showError(message) {
        if (!this.errorEl) return;
        if (this.errorMsg) this.errorMsg.textContent = message;
        this.errorEl.hidden = false;
      }

      hideError() {
        if (this.errorEl) this.errorEl.hidden = true;
      }
    }
    customElements.define('product-form', ProductForm);
  }

  // ─── Gallery: scroll-snap carousel + dots + thumbs ────────────────
  document.addEventListener('DOMContentLoaded', initGalleries);
  if (document.readyState !== 'loading') initGalleries();

  function initGalleries() {
    document.querySelectorAll('.section-main-product').forEach(initGallery);
  }

  function initGallery(section) {
    if (section.dataset.galleryReady) return;
    section.dataset.galleryReady = 'true';

    const track = section.querySelector('[data-gallery-track]');
    const slides = Array.from(section.querySelectorAll('[data-slide]'));
    const thumbs = Array.from(section.querySelectorAll('[data-thumb]'));
    const prevBtn = section.querySelector('[data-gallery-prev]');
    const nextBtn = section.querySelector('[data-gallery-next]');

    if (!track || slides.length === 0) return;

    let activeIndex = 0;

    /**
     * Scrolls the carousel to a specific slide. We update activeIndex
     * immediately (not just after the smooth-scroll settles) so that
     * rapid arrow clicks compose correctly — otherwise clicking next
     * twice in a row stays on slide 1 because the second click's
     * `activeIndex + 1` reads a stale 0.
     */
    function scrollToIndex(index) {
      const slide = slides[index];
      if (!slide) return;
      setActiveUI(index);
      track.scrollTo({
        left: slide.offsetLeft,
        behavior: 'smooth'
      });
    }

    /**
     * Updates the visual "active" state of thumbs, and auto-scrolls the
     * thumb strip so the active thumb stays in view on mobile.
     */
    function setActiveUI(index) {
      activeIndex = index;
      thumbs.forEach((t, i) => t.classList.toggle('is-active', i === index));

      // Keep the active thumb visible inside its scroll container.
      const activeThumb = thumbs[index];
      if (activeThumb && activeThumb.parentElement) {
        const strip = activeThumb.parentElement;
        if (strip.scrollWidth > strip.clientWidth) {
          const t = activeThumb;
          const leftEdge = t.offsetLeft - strip.offsetLeft;
          const rightEdge = leftEdge + t.offsetWidth;
          if (leftEdge < strip.scrollLeft) {
            strip.scrollTo({ left: leftEdge - 8, behavior: 'smooth' });
          } else if (rightEdge > strip.scrollLeft + strip.clientWidth) {
            strip.scrollTo({ left: rightEdge - strip.clientWidth + 8, behavior: 'smooth' });
          }
        }
      }

      // Toggle arrow disabled state at the ends.
      if (prevBtn) prevBtn.disabled = index <= 0;
      if (nextBtn) nextBtn.disabled = index >= slides.length - 1;
    }

    // ── User input → scroll the carousel ──
    thumbs.forEach((t, i) => t.addEventListener('click', () => scrollToIndex(i)));
    if (prevBtn) prevBtn.addEventListener('click', () => scrollToIndex(activeIndex - 1));
    if (nextBtn) nextBtn.addEventListener('click', () => scrollToIndex(activeIndex + 1));

    // ── Carousel scroll → update active thumb ──
    // Each slide is `flex: 0 0 100%` of the track width, so scrollLeft
    // divided by track width gives a fractional index. Rounding lands on
    // the closest snap point — which is exactly where the carousel will
    // settle thanks to `scroll-snap-type: x mandatory`. Updates fire
    // during the swipe AND after it settles. rAF-throttled so we don't
    // do redundant work mid-scroll.
    let scrollRaf = null;
    track.addEventListener('scroll', () => {
      if (scrollRaf) return;
      scrollRaf = requestAnimationFrame(() => {
        scrollRaf = null;
        const slideWidth = track.clientWidth;
        if (!slideWidth) return;
        const newIndex = Math.round(track.scrollLeft / slideWidth);
        if (newIndex !== activeIndex && newIndex >= 0 && newIndex < slides.length) {
          setActiveUI(newIndex);
        }
      });
    }, { passive: true });

    // Expose a programmatic API for variant-selects.updateImage() to jump
    // the carousel to the slide matching the selected variant's image.
    window.__dahabeyaProductGallery = scrollToIndex;

    // Initial state.
    setActiveUI(0);
  }
})();
